/**
 * Public, no-login candidate page that opens the 100ms Prebuilt room.
 *
 * URL: /entrevista-video/:interviewId?code=ABCDEF
 *
 * Flow:
 * 1. POST `/video/public/join-url` with the access code from the URL.
 * 2. Embed the resulting Prebuilt URL in an iframe.
 */
import axios from "axios";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2, Video as VideoIcon } from "lucide-react";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import VideoInterviewRoom from "@/components/VideoInterviewRoom";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PublicVideoInterviewPage = () => {
  const { interviewId } = useParams();
  const [searchParams] = useSearchParams();
  const code = (searchParams.get("code") || "").trim();

  const [phase, setPhase] = useState("welcome"); // welcome | joining | in-call | ended | error
  const [joinUrl, setJoinUrl] = useState("");
  const [error, setError] = useState("");

  useDocumentTitle(
    phase === "in-call" ? "🔴 Entrevista em andamento" : "Sua entrevista por vídeo",
    { exact: true },
  );

  const handleStart = async () => {
    setError("");
    if (!code) {
      setError("Link de acesso inválido. Solicite ao recrutador um novo link.");
      return;
    }
    setPhase("joining");
    try {
      const res = await axios.post(
        `${API}/video/public/join-url?interview_id=${encodeURIComponent(interviewId)}`,
        { access_code: code }
      );
      setJoinUrl(res.data.join_url);
      setPhase("in-call");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Não foi possível acessar a entrevista. Verifique o link."
      );
      setPhase("welcome");
    }
  };

  if (phase === "in-call" && joinUrl) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-950" data-testid="public-video-page">
        <header className="px-4 py-3 bg-slate-900 border-b border-slate-700 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-blue flex items-center justify-center text-white font-bold">
            GR
          </div>
          <div>
            <div className="text-sm text-white font-semibold">GridRecruiter</div>
            <div className="text-[11px] text-slate-400">Entrevista por vídeo</div>
          </div>
        </header>
        <VideoInterviewRoom joinUrl={joinUrl} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-6">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md w-full shadow-2xl">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-brand-blue flex items-center justify-center text-white font-bold">
            GR
          </div>
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">
              GridRecruiter
            </div>
            <div className="text-base text-white font-semibold">
              Entrevista por vídeo
            </div>
          </div>
        </div>

        <h1 className="heading text-xl font-bold text-white mb-2">
          Bem-vindo(a) à sua entrevista
        </h1>
        <p className="text-sm text-slate-400 mb-6 leading-relaxed">
          A chamada acontece direto no navegador — não precisa instalar nada.
          A entrevista será gravada para fins de seleção.
        </p>

        {error && (
          <div className="mb-4 flex items-start gap-2 px-3 py-2 rounded-md bg-rose-950/60 border border-rose-800 text-rose-200 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          onClick={handleStart}
          disabled={phase === "joining"}
          className="w-full h-11 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold"
          data-testid="public-start-btn"
        >
          {phase === "joining" ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <VideoIcon className="w-4 h-4 mr-2" />
          )}
          Entrar na entrevista
        </Button>

        <p className="text-[11px] text-slate-500 mt-4 text-center">
          Ao entrar, você concorda com a gravação da entrevista para fins de
          análise pela equipe de recrutamento.
        </p>
      </div>
    </div>
  );
};

export default PublicVideoInterviewPage;
